/* eslint-disable yoda, max-lines */
import { getCMSConfig, getPreMarketingAvcResult } from '../cms';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { getOptionPageUrl, getProductPageUrl, getUrl } from '../url';
import { PreMarketingType, ProductType } from '../../enums/product';
import { IButtonProperties } from '@vfde-brix/ws10/button';
import { UserInfo } from 'Container/App/interface';
import { AvcResult, JarvisProduct } from '@vfde-sails/avc-core';
import { Domains, LandingPagePath } from '../../enums/network';

export const getPreMarketingBtnLink = (preMarketingType: PreMarketingType) => {
    switch (preMarketingType) {
        case PreMarketingType.BHS:
            return getCMSConfig()?.offerSummaryCard?.button.hotline.link;
        case PreMarketingType.BKV:
        case PreMarketingType.PRE_FIBER:
            return getUrl(Domains.VF, LandingPagePath.PRE_FIBER);
    }
};

/**
 * Gets the props that need to be
 * updated for the price button
 * @param tariffId - Id of the selected tariff
 * @param isGigakombi - Gigakombi chosen or not
 * @param avcResult - Result of the avc, if there is
 * @param onClick - OnClick event
 * @returns Props to update
 */
export const getUpdatedPriceButtonProps = (
    tariff: JarvisProduct | undefined,
    isGigakombi: boolean,
    avcResult?: AvcResult,
    onClick?: Function,
    preMarketingType?: PreMarketingType,
    userInfo?: UserInfo,
): Partial<IButtonProperties> => {
    const offerPriceConfig = getCMSConfig()?.offerSummaryCard;
    const avcConfig = getCMSConfig()?.avcResult;
    const loggedCasesConfig = getCMSConfig()?.loggedCases;

    let linkHref: string | undefined;
    let stdContent: string | undefined;

    // Handel CTA on user info change
    if (loggedCasesConfig && userInfo?.isLogged && userInfo?.isFixed){

        if (userInfo.isUM || userInfo.isVKD){
            linkHref = loggedCasesConfig?.multipleContract.url;
            stdContent = loggedCasesConfig?.multipleContract.text;
        } else {
            linkHref = loggedCasesConfig?.dsl.url;
            stdContent = loggedCasesConfig?.dsl.text;
        }

        return {
            stdContent,
            linkHref,
            business: NO_PATTERN_BUSINESS_LOGIC,
        };
    }

    if (!offerPriceConfig || !avcConfig || !tariff?.id) {
        return {};
    }

    if (!avcResult) {
        return {
            stdContent: offerPriceConfig.button.avc,
            linkHref: undefined,
            business: { onClick },
        };
    }

    if (preMarketingType) {
        const preMarketingConfig = getPreMarketingAvcResult(preMarketingType);

        return {
            business: NO_PATTERN_BUSINESS_LOGIC,
            stdContent: preMarketingConfig?.button,
            linkHref: getPreMarketingBtnLink(preMarketingType),
        };
    }

    switch (avcResult.fastest?.productType) {
        case ProductType.FIBRE:
            linkHref = getOptionPageUrl(tariff.id, isGigakombi);
            stdContent = offerPriceConfig.button.option;
            break;
        case ProductType.CABLE:
        case ProductType.DSL:
            linkHref = getProductPageUrl(avcResult.fastest.productType, avcResult.fastest.id);
            stdContent = offerPriceConfig.button.product;
            break;
        default:
            linkHref = avcConfig.gigacube.link;
            stdContent = offerPriceConfig.button.product;
            break;
    }

    return { stdContent, linkHref, business: NO_PATTERN_BUSINESS_LOGIC };
};

/**
 * Returns the updated upsell result button for the pre-marketing cases.
 */
export const getPreMarketingResultUpsellBtnProps = (tariff: JarvisProduct): Partial<IButtonProperties> => {
    const props: Partial<IButtonProperties> = {
        business: NO_PATTERN_BUSINESS_LOGIC,
    };

    switch (tariff.productType) {
        case 'cable':
            props.stdContent = getCMSConfig()?.avcResult?.cable.button;
            props.linkHref = getProductPageUrl(tariff.productType, tariff.id);
            break;
        case 'dsl':
            props.stdContent = getCMSConfig()?.avcResult?.dsl.button;
            props.linkHref = getProductPageUrl(tariff.productType, tariff.id);
            break;
        default:
            const { button, link } = getCMSConfig()?.avcResult?.gigacube || {};

            props.stdContent = button;
            props.linkHref = link;
            break;
    }

    return props;
};

export const getPreMarketingPriceUpsellBtnProps = (tariff: JarvisProduct): Partial<IButtonProperties> => {
    const props: Partial<IButtonProperties> = {
        business: NO_PATTERN_BUSINESS_LOGIC,
    };

    switch (tariff.productType) {
        case 'cable':
            props.stdContent = getCMSConfig()?.offerSummaryCard?.alternativeButton.cable;
            props.linkHref = getProductPageUrl(tariff.productType, tariff.id);
            break;
        case 'dsl':
            props.stdContent = getCMSConfig()?.offerSummaryCard?.alternativeButton.dsl;
            props.linkHref = getProductPageUrl(tariff.productType, tariff.id);
            break;
        default:
            const { text, link } = getCMSConfig()?.offerSummaryCard?.alternativeButton.gigacube || {};

            props.stdContent = text;
            props.linkHref = link;
            break;
    }

    return props;
};
